import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { MfaChallenge } from './MfaChallenge.js';
import { signInV1, signInV2 } from './signInStylesV2.js';
import { getAuthErrorCode } from './mfaPure.js';
import { validateSignInForm, mapAuthErrorMessage, } from './signInPure.js';
const DEFAULT_DESCRIPTION = '管理者アカウントでサインインしてください';
const DEFAULT_ACCOUNT_NOTE = 'アカウントは管理者が発行します。アカウントの発行・パスワードの再設定は、貴社の管理者にお問い合わせください。';
const COPYRIGHT_HOLDER = 'カワイ株式会社';
/**
 * S-89: ブランド 3 層ヘッダー（TracePit ロゴ ＞ テナント名 ＞ アプリ名）。
 * SaaS 外販時はテナント名の差し替えだけで済むよう階層を固定する。
 */
function BrandHeaderView({ logoSrc, logoAlt, tenantName, appTitle, titleAs, st, }) {
    const Title = titleAs;
    return (_jsxs("header", { style: st.header, children: [logoSrc && _jsx("img", { src: logoSrc, alt: logoAlt, style: st.logo }), tenantName && _jsx("p", { style: st.tenantName, children: tenantName }), _jsx(Title, { style: st.appTitle, children: appTitle })] }));
}
/**
 * S-89: 管理系 5 app 共通のサインインカード。
 *
 * 従来各 app にコピーされていた SignInPage の本体。mfa-required 分岐 →
 * MfaChallenge 表示までを内部で処理する。サインイン成功後の遷移
 * （onAuthStateChanged → Navigate）は各 app の SignInPage 側の関心事。
 *
 * 表示ポリシー（設計経緯は issue S-89 参照）:
 * - パスワードリセット・サインアップの「リンク」は置かない（機能が無いため、
 *   行き止まりリンクを作らず accountNote の運用文言で案内する）
 * - 利用規約 / プライバシーポリシーのリンクは公開 URL 確定まで載せない
 * - エラー文言はアカウントの存在有無を漏らさない（mapAuthErrorMessage）
 */
export function SignInCard({ logoSrc, logoAlt = 'TracePit', tenantName, appTitle, description = DEFAULT_DESCRIPTION, notice, accountNote = DEFAULT_ACCOUNT_NOTE, contactEmail, signIn, variant = 'v1', }) {
    const st = variant === 'v2' ? signInV2 : signInV1;
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    // S-75: MFA 登録済アカウントは signIn が mfa-required を返す → challenge を表示。
    const [mfaResolver, setMfaResolver] = useState(null);
    if (mfaResolver) {
        return (_jsx(MfaChallenge, { resolver: mfaResolver, onCancel: () => setMfaResolver(null), header: _jsx(BrandHeaderView, { st: st, logoSrc: logoSrc, logoAlt: logoAlt, tenantName: tenantName, appTitle: appTitle, titleAs: "p" }) }));
    }
    async function onSubmit(e) {
        e.preventDefault();
        const v = validateSignInForm(email, password);
        setErrors(v);
        setSubmitError(null);
        if (v.email || v.password)
            return;
        setSubmitting(true);
        try {
            const outcome = await signIn(email.trim(), password);
            if (outcome.kind === 'mfa-required') {
                setMfaResolver(outcome.resolver);
            }
            // 'signed-in' は onAuthStateChanged → 各 app の SignInPage が Navigate する。
        }
        catch (err) {
            setSubmitError(mapAuthErrorMessage(getAuthErrorCode(err)));
        }
        finally {
            setSubmitting(false);
        }
    }
    return (_jsxs("main", { style: st.wrap, className: st.rootClassName, children: [_jsxs("div", { style: st.card, children: [_jsx(BrandHeaderView, { st: st, logoSrc: logoSrc, logoAlt: logoAlt, tenantName: tenantName, appTitle: appTitle, titleAs: "h1" }), _jsx("p", { style: st.subtitle, children: description }), notice, _jsxs("form", { style: st.form, onSubmit: onSubmit, noValidate: true, children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "signin-email", style: st.label, children: "\u30E1\u30FC\u30EB\u30A2\u30C9\u30EC\u30B9" }), _jsx("input", { id: "signin-email", type: "email", autoComplete: "email", required: true, value: email, onChange: (e) => setEmail(e.target.value), style: st.input, "aria-invalid": Boolean(errors.email), "aria-describedby": errors.email ? 'signin-email-error' : undefined }), errors.email && (_jsx("p", { id: "signin-email-error", role: "alert", style: st.fieldError, children: errors.email }))] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "signin-password", style: st.label, children: "\u30D1\u30B9\u30EF\u30FC\u30C9" }), _jsx("input", { id: "signin-password", type: "password", autoComplete: "current-password", required: true, value: password, onChange: (e) => setPassword(e.target.value), style: st.input, "aria-invalid": Boolean(errors.password), "aria-describedby": errors.password ? 'signin-password-error' : undefined }), errors.password && (_jsx("p", { id: "signin-password-error", role: "alert", style: st.fieldError, children: errors.password }))] }), submitError && (_jsx("p", { role: "alert", style: st.error, children: submitError })), _jsx("button", { type: "submit", disabled: submitting, style: st.button(submitting), children: submitting ? 'サインイン中…' : 'サインイン' })] }), _jsx("p", { style: st.accountNote, children: accountNote })] }), _jsxs("footer", { style: st.footer, children: [_jsxs("p", { style: { margin: 0 }, children: ["\u00A9 ", new Date().getFullYear(), " ", COPYRIGHT_HOLDER] }), contactEmail && (_jsxs("p", { style: { margin: 0 }, children: ["\u304A\u554F\u3044\u5408\u308F\u305B:", ' ', _jsx("a", { href: `mailto:${contactEmail}`, style: st.footerLink, children: contactEmail })] }))] })] }));
}
//# sourceMappingURL=SignInCard.js.map